import { api } from '@/shared/api';
import type {
  Announcement as AnnouncementType,
  ChangeAnnouncementStatusPayload,
  ChangeAnnouncementStatusResponse,
  CreateAnnouncementRequest,
  DeleteAnnouncementResponse,
  ResidentAnnouncementDetails,
  UpdateAnnouncementRequest,
} from '../types';
import type { ApiResponse, Pagination } from '@/shared/types/api';
import type { AnnouncementFiltersType } from '../components/AnnouncementFilters';

export const announcementApi = {
  // Get all announcements
  // getAnnouncements: async (): Promise<AnnouncementType[]> => {
  //   return api.get<AnnouncementType[]>('/announcements');
  // },

  // Get announcement by ID
  // getAnnouncementById: async (id: string): Promise<Announcement> => {
  //   return api.get<Announcement>(`/announcements/${id}`);
  // },

  // Create new announcement
  createAnnouncement: async (
    data: CreateAnnouncementRequest,
  ): Promise<AnnouncementType> => {
    return api.post<AnnouncementType>('/announcements', data);
  },

  // Update announcement
  updateAnnouncement: async (
    id: string,
    data: UpdateAnnouncementRequest,
  ): Promise<AnnouncementType> => {
    return api.put<AnnouncementType>(`/announcements/${id}`, data);
  },

  // Delete announcement
  // deleteAnnouncement: async (id: string): Promise<void> => {
  //   return api.delete<void>(`/announcements/${id}`);
  // },

  // Mark announcement as read
  markAsRead: async (id: string): Promise<void> => {
    return api.post<void>(`/announcements/${id}/read`);
  },

  // Get announcements by house
  getAnnouncementsByHouse: async (houseId: string): Promise<Announcement[]> => {
    return api.get<Announcement[]>(`/announcements?houseId=${houseId}`);
  },

  // Get announcements by priority
  getAnnouncementsByPriority: async (
    priority: string,
  ): Promise<Announcement[]> => {
    return api.get<Announcement[]>(`/announcements?priority=${priority}`);
  },

  /**
   *
   * @param filters
   * @returns
   */
  getUserAnnouncements: async (
    filters: AnnouncementFiltersType,
  ): Promise<AnnouncementsResponse> => {
    const res = await api.get<ApiResponse<AnnouncementsResponse>>(
      `/residents/dashboard/announcements`,
      {
        params: { ...filters },
      },
    );
    return res.data;
  },

  getAnnouncementById: async (
    id: string,
  ): Promise<ResidentAnnouncementDetails> => {
    const res = await api.get<ApiResponse<ResidentAnnouncementDetails>>(
      `/announcements/${id}`,
    );
    return res.data;
  },

  // ✅ Delete announcement
  deleteAnnouncement: async (
    id: string,
  ): Promise<DeleteAnnouncementResponse> => {
    const res = await api.delete<DeleteAnnouncementResponse>(
      `/announcements/${id}`,
    );
    return res;
  },

  // ✅ Change announcement status
  changeAnnouncementStatus: async (
    id: string,
    payload: ChangeAnnouncementStatusPayload,
  ): Promise<ChangeAnnouncementStatusResponse> => {
    const res = await api.patch<ChangeAnnouncementStatusResponse>(
      `/announcements/${id}/status`,
      payload,
    );
    return res;
  },

  /**********************************
   * ANNOUNCEMENT APIS
   **********************************/
  async getOverview(timeRange: number): Promise<AnnouncementOverviewResponse> {
    const response = await api.get<ApiResponse<AnnouncementOverviewResponse>>(
      `/announcements/overview?timeRange=${timeRange}`,
    );
    return response.data;
  },

  getAnnouncements: async (
    filters: AnnouncementFiltersType,
  ): Promise<AnnouncementsResponse> => {
    const res = await api.get<ApiResponse<AnnouncementsResponse>>(
      `/announcements`,
      {
        params: filters ,
      },
    );
    return res.data;
  },

  async create(data: CreateAnnouncementPayload): Promise<Announcement> {
    const response = await api.post<ApiResponse<Announcement>>(
      `/announcements`,
      data,
    );

    if (!response.success) {
      throw new Error(response.message || 'Failed to create announcement');
    }
    return response.data;
  },
};

export interface AnnouncementOverviewResponse {
  totalAnnouncements: number;
  activeCount: number;
  scheduledCount: number;
  expiredCount: number;
  activeAudience: number;
  latestAnnouncements: Announcement[];
}
export interface AnnouncementsResponse {
  announcements: Announcement[];
  pagination: Pagination;
}

export interface Announcement {
  id: string;
  title: string;
  description: string;
  category: string;
  priority?: string;
  scheduleTime?: string;
  fileUrl?: string | null;
  createdAt?: string;
  status: 'PUBLISHED' | 'EXPIRED' | 'SCHEDULED' | 'DRAFT' | 'RESOLVED';
  postedBy?: {
    id: string;
    name: string;
    role: string;
  };
  houses?: AnnouncementHouse;
}

export interface AnnouncementHouse {
  id: string;
  name: string;
}

export type PriorityType = 'URGENT' | 'HIGH' | 'NORMAL' | 'LOW';

export interface CreateAnnouncementPayload {
  title: string;
  description: string;
  category: string;
  priority: PriorityType;
  file?: string;
  audience: string[];
  house: string[];
  scheduleTime?: string;
  status: 'DRAFT' | 'PUBLISHED' | 'SCHEDULED';
}
